import * as React from 'react';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type LexicalEditor,
  type NodeKey,
} from 'lexical';
import styles from '../lexical-editor.module.css';
import { $isImageNode } from './ImageNode';

type ResizeDirection = 'ne' | 'nw' | 'se' | 'sw';

interface ImageComponentProps {
  altText: string;
  editor: LexicalEditor;
  height: 'auto' | number;
  nodeKey: NodeKey;
  src: string;
  width: 'inherit' | number;
}

export function ImageComponent({
  altText,
  editor,
  height,
  nodeKey,
  src,
  width,
}: ImageComponentProps) {
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const [isResizing, setIsResizing] = React.useState(false);
  const [size, setSize] = React.useState({
    height,
    width,
  });
  const [naturalRatio, setNaturalRatio] = React.useState<number | null>(null);
  const cleanupResizeRef = React.useRef<(() => void) | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const liveSizeRef = React.useRef(size);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const handleImageLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalHeight, naturalWidth } = event.currentTarget;

      if (naturalWidth > 0 && naturalHeight > 0) {
        setNaturalRatio(naturalWidth / naturalHeight);
      }
    },
    []
  );

  React.useEffect(() => {
    setSize({ height, width });
    liveSizeRef.current = { height, width };
  }, [height, width]);

  React.useEffect(() => {
    return () => {
      cleanupResizeRef.current?.();
    };
  }, []);

  const removeImage = React.useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);

      if ($isImageNode(node)) {
        node.remove();
      }
    });
  }, [editor, nodeKey]);

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const target = event.target;

          if (
            target instanceof HTMLElement &&
            wrapperRef.current?.contains(target)
          ) {
            event.preventDefault();

            if (!event.shiftKey) {
              clearSelection();
            }

            setSelected(!isSelected);
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          const target = event.target;

          if (
            target instanceof HTMLElement &&
            wrapperRef.current?.contains(target)
          ) {
            event.preventDefault();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        () => {
          if (!isSelected) {
            return false;
          }

          const selection = $getSelection();

          if ($isNodeSelection(selection)) {
            removeImage();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        () => {
          if (!isSelected) {
            return false;
          }

          const selection = $getSelection();

          if ($isNodeSelection(selection)) {
            removeImage();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [clearSelection, editor, isSelected, removeImage, setSelected, nodeKey]);

  const updateNodeSize = React.useCallback(
    (nextWidth: number, nextHeight: number) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);

        if ($isImageNode(node)) {
          node.setWidth(Math.round(nextWidth));
          node.setHeight(Math.round(nextHeight));
        }
      });
    },
    [editor, nodeKey]
  );

  const startResize = React.useCallback(
    (direction: ResizeDirection, startEvent: React.PointerEvent) => {
      if (!imageRef.current) {
        return;
      }

      startEvent.preventDefault();
      startEvent.stopPropagation();
      const handleElement = startEvent.currentTarget as HTMLButtonElement;
      handleElement.setPointerCapture(startEvent.pointerId);

      const rect = imageRef.current.getBoundingClientRect();
      const startWidth = rect.width;
      const startHeight = rect.height;
      const startClientX = startEvent.clientX;
      const startClientY = startEvent.clientY;
      // Scale by the file's own ratio, not the rendered box's: deriving it from
      // the box makes a already-squeezed image keep its distortion forever.
      const aspectRatio = naturalRatio ?? startWidth / Math.max(startHeight, 1);
      const editorRootWidth =
        editor.getRootElement()?.getBoundingClientRect().width ?? startWidth;
      const maxWidth = Math.max(editorRootWidth - 32, startWidth);

      cleanupResizeRef.current?.();
      setIsResizing(true);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const horizontalDirection =
          direction === 'ne' || direction === 'se' ? 1 : -1;
        const deltaX =
          (moveEvent.clientX - startClientX) * horizontalDirection;
        const deltaY =
          (moveEvent.clientY - startClientY) *
          (direction === 'se' || direction === 'sw' ? 1 : -1);
        const widthFromX = startWidth + deltaX;
        const widthFromY = startWidth + deltaY * aspectRatio;
        const unclampedWidth = Math.abs(deltaX) >= Math.abs(deltaY * aspectRatio)
          ? widthFromX
          : widthFromY;
        const nextWidth = Math.min(Math.max(unclampedWidth, 24), maxWidth);
        const nextHeight = Math.max(nextWidth / aspectRatio, 24);
        const nextSize = {
          height: nextHeight,
          width: nextWidth,
        };

        liveSizeRef.current = nextSize;
        setSize(nextSize);
      };

      const stopResize = () => {
        const nextWidth =
          typeof liveSizeRef.current.width === 'number'
            ? liveSizeRef.current.width
            : startWidth;
        const nextHeight =
          typeof liveSizeRef.current.height === 'number'
            ? liveSizeRef.current.height
            : startHeight;

        updateNodeSize(nextWidth, nextHeight);
        setIsResizing(false);
        if (handleElement.hasPointerCapture(startEvent.pointerId)) {
          handleElement.releasePointerCapture(startEvent.pointerId);
        }
        globalThis.window.removeEventListener('pointermove', handlePointerMove);
        globalThis.window.removeEventListener('pointerup', stopResize);
        globalThis.window.removeEventListener('pointercancel', stopResize);
        globalThis.window.removeEventListener('blur', stopResize);
        cleanupResizeRef.current = null;
      };

      cleanupResizeRef.current = stopResize;
      globalThis.window.addEventListener('pointermove', handlePointerMove);
      globalThis.window.addEventListener('pointerup', stopResize);
      globalThis.window.addEventListener('pointercancel', stopResize);
      globalThis.window.addEventListener('blur', stopResize);
    },
    [naturalRatio, updateNodeSize]
  );

  // Once the file's own proportions are known let the browser derive the height:
  // a stored height that disagrees with them renders the image squeezed, because
  // `max-width: 100%` clamps the width while the fixed height stays put.
  const imageStyle: React.CSSProperties = {
    height:
      naturalRatio === null && size.height !== 'auto'
        ? `${size.height}px`
        : 'auto',
    width: size.width === 'inherit' ? 'auto' : `${size.width}px`,
  };

  return (
    <span
      className={styles.imageInlineContainer}
      data-selected={isSelected}
      ref={wrapperRef}
    >
      <img
        alt={altText}
        className={styles.image}
        draggable={false}
        onLoad={handleImageLoad}
        ref={imageRef}
        src={src}
        style={imageStyle}
      />
      {isSelected && editor.isEditable() && (
        <div className={styles.imageResizerOverlay}>
          <ResizeHandle direction="nw" onPointerDown={startResize} />
          <ResizeHandle direction="ne" onPointerDown={startResize} />
          <ResizeHandle direction="sw" onPointerDown={startResize} />
          <ResizeHandle direction="se" onPointerDown={startResize} />
        </div>
      )}
      {isResizing && <div className={styles.imageResizingMask} />}
    </span>
  );
}

function ResizeHandle({
  direction,
  onPointerDown,
}: {
  direction: ResizeDirection;
  onPointerDown: (
    direction: ResizeDirection,
    event: React.PointerEvent
  ) => void;
}) {
  return (
    <button
      className={styles.imageResizeHandle}
      data-direction={direction}
      onPointerDown={(event) => {
        onPointerDown(direction, event);
      }}
      type="button"
    />
  );
}
